/**
 * Coolman Workforce · Holiday Management — landing page tab.
 *
 * Product Owner directive 2026-07-03. Combines an operational overview
 * (Provider · Province · Last Refresh · Current + Next Year status ·
 * Upcoming Statutory · Upcoming Company) with the full administrative
 * CRUD surface for Statutory and Company holidays. The Settings tab
 * retains Event Types only.
 *
 * Design principles:
 *   - Overview tiles surface the 6 required signals in a scannable grid.
 *   - Quick Actions are hoisted to the top so admins never need to
 *     scroll to refresh a year or add a Company Holiday.
 *   - Statutory & Company sections mirror the terminology directive
 *     ("two distinct sections") and enforce the category-driven
 *     behaviour (statutory = disable-only, company = full CRUD).
 *   - Colour badges use the approved Calendar Colour Standard.
 *   - Refresh Audit trail (spec 12 · PO directive 2026-07-03) surfaces
 *     the last 10 refresh events.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowsClockwise, Plus, Info, MapPin, Clock, Calendar as CalendarIcon,
  CheckCircle, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { workforceApi, formatDate } from "./workforceApi";

const CATEGORY_BADGE = {
  statutory: { label: "Statutory", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  company:   { label: "Company",   tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function HolidaysTab({ holidays, onReload, readOnly }) {
  const [overview, setOverview] = useState(null);
  const [refreshes, setRefreshes] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  const [refreshingYear, setRefreshingYear] = useState(null);
  const [holForm, setHolForm] = useState({ date: "", name: "" });
  const mountedRef = useRef(true);

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true);
    setOverviewError(null);
    try {
      const [ov, rf] = await Promise.all([
        workforceApi.holidayOverview(),
        workforceApi.holidayRefreshes ? workforceApi.holidayRefreshes() : Promise.resolve({ data: [] }),
      ]);
      if (!mountedRef.current) return;
      setOverview(ov.data);
      setRefreshes(rf.data || []);
    } catch (e) {
      if (!mountedRef.current) return;
      setOverviewError(e?.response?.data?.detail || e?.message || "Failed to load overview");
    } finally {
      if (mountedRef.current) setLoadingOverview(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadOverview();
    return () => { mountedRef.current = false; };
  }, [loadOverview]);

  const statutory = useMemo(() => holidays.filter((h) => (h.category || (h.is_statutory ? "statutory" : "company")) === "statutory"), [holidays]);
  const company = useMemo(() => holidays.filter((h) => (h.category || (h.is_statutory ? "statutory" : "company")) === "company"), [holidays]);
  const statutoryYears = useMemo(() => {
    const cy = new Date().getFullYear();
    // Cap Quick Actions to CY + NY to avoid stale years (e.g. test
    // residue) leaking into the admin surface.
    const allYears = Array.from(new Set(statutory.map((h) => h.year).filter(Boolean)));
    return allYears.filter((y) => y === cy || y === cy + 1).sort();
  }, [statutory]);

  const refreshYear = async (year) => {
    setRefreshingYear(year);
    try {
      const res = await workforceApi.refreshHolidays(year);
      const { inserted, updated, disabled, skipped_manual } = res.data;
      toast.success(`Refreshed ${year}: +${inserted} · Δ${updated} · ×${disabled}${skipped_manual ? ` · skip ${skipped_manual}` : ""}`);
      await Promise.all([onReload(), loadOverview()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingYear(null);
    }
  };

  const addHoliday = async () => {
    if (!holForm.date || !holForm.name.trim()) return toast.error("Date and name required");
    try {
      await workforceApi.saveHoliday({ ...holForm, region: "ON", active: true });
      toast.success("Company Holiday added");
      setHolForm({ date: "", name: "" });
      await Promise.all([onReload(), loadOverview()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (h) => {
    try {
      await workforceApi.patchHoliday(h.id, { active: !h.active });
      toast.success(`${h.name} ${!h.active ? "enabled" : "disabled"}`);
      await Promise.all([onReload(), loadOverview()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const removeHoliday = async (id) => {
    try {
      await workforceApi.deleteHoliday(id);
      toast.success("Company Holiday removed");
      await Promise.all([onReload(), loadOverview()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div data-testid="workforce-holidays-tab" className="space-y-6">
      {/* Overview tiles */}
      <section aria-label="Holiday overview" data-testid="wf-holidays-overview" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <OverviewTile
          testid="wf-holidays-tile-provider"
          icon={Info}
          label="Provider"
          primary={overview?.provider?.label || "—"}
          secondary={overview?.provider?.data_source ? `Source: ${overview.provider.data_source}` : null}
          loading={loadingOverview}
        />
        <OverviewTile
          testid="wf-holidays-tile-province"
          icon={MapPin}
          label="Province"
          primary={overview?.province?.subdivision || "—"}
          secondary={overview?.province?.country ? `Country: ${overview.province.country}` : null}
          loading={loadingOverview}
        />
        <OverviewTile
          testid="wf-holidays-tile-last-refresh"
          icon={Clock}
          label="Last Refresh"
          primary={overview?.last_refresh ? formatRelativeTs(overview.last_refresh) : "Never"}
          secondary={overview?.last_refresh ? new Date(overview.last_refresh).toLocaleString() : null}
          loading={loadingOverview}
        />
        <OverviewTile
          testid="wf-holidays-tile-year-status"
          icon={CalendarIcon}
          label={`Next Year (${overview?.next_year?.year ?? "—"})`}
          primary={overview?.next_year?.populated ? "Populated" : "Not populated"}
          secondary={overview?.next_year ? `${overview.next_year.count} of ${overview.next_year.expected} statutory holidays` : null}
          loading={loadingOverview}
          tone={overview?.next_year?.populated ? "ok" : "warn"}
        />
      </section>

      {/* Quick actions */}
      {!readOnly && (
        <section data-testid="wf-holidays-quick-actions" className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Quick Actions</h3>
            <button
              type="button"
              onClick={loadOverview}
              data-testid="wf-holidays-overview-refresh"
              disabled={loadingOverview}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              title="Refresh overview"
            >
              <ArrowsClockwise size={14} weight="bold" className={loadingOverview ? "animate-spin" : ""} />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {statutoryYears.map((yr) => (
              <button
                key={yr}
                type="button"
                data-testid={`wf-holidays-quickaction-refresh-${yr}`}
                onClick={() => refreshYear(yr)}
                disabled={refreshingYear === yr}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <ArrowsClockwise size={12} weight="bold" className={refreshingYear === yr ? "animate-spin" : ""} />
                {refreshingYear === yr ? `Refreshing ${yr}…` : `Refresh ${yr}`}
              </button>
            ))}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <input
                data-testid="wf-holidays-add-date"
                type="date"
                value={holForm.date}
                onChange={(e) => setHolForm({ ...holForm, date: e.target.value })}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
              />
              <input
                data-testid="wf-holidays-add-name"
                value={holForm.name}
                onChange={(e) => setHolForm({ ...holForm, name: e.target.value })}
                placeholder="Company holiday name"
                className="w-48 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
              />
              <button
                type="button"
                data-testid="wf-holidays-add-submit"
                onClick={addHoliday}
                className="inline-flex items-center gap-1 rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-xs font-semibold text-ink-inverse hover:bg-slate-900"
              >
                <Plus size={12} weight="bold" /> Add Company Holiday
              </button>
            </div>
          </div>
          {overviewError && (
            <div className="mt-2 text-xs text-rose-700">{overviewError}</div>
          )}
        </section>
      )}

      {/* Upcoming panels */}
      <section className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <UpcomingPanel
          testid="wf-holidays-upcoming-statutory"
          title="Upcoming Statutory Holidays"
          category="statutory"
          items={overview?.upcoming_statutory || []}
          loading={loadingOverview}
        />
        <UpcomingPanel
          testid="wf-holidays-upcoming-company"
          title="Upcoming Company Holidays"
          category="company"
          items={overview?.upcoming_company || []}
          loading={loadingOverview}
        />
      </section>

      {/* Full CRUD sections */}
      <section data-testid="wf-holidays-manage" className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ManagementSection
          testid="wf-holiday-statutory-section"
          title="Statutory Holidays"
          category="statutory"
          items={statutory}
          readOnly={readOnly}
          onToggle={toggleActive}
        />
        <ManagementSection
          testid="wf-holiday-company-section"
          title="Company Holidays"
          category="company"
          items={company}
          readOnly={readOnly}
          onToggle={toggleActive}
          onRemove={removeHoliday}
        />
      </section>

      {/* Refresh audit log */}
      <section data-testid="wf-holidays-refresh-audit" className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Refresh Audit</h3>
        {refreshes.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500">
            No refresh events recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="pb-1 pr-3 font-medium">When</th>
                  <th className="pb-1 pr-3 font-medium">Actor</th>
                  <th className="pb-1 pr-3 font-medium">Provider</th>
                  <th className="pb-1 pr-3 font-medium">Year</th>
                  <th className="pb-1 pr-3 font-medium text-right">Added</th>
                  <th className="pb-1 pr-3 font-medium text-right">Updated</th>
                  <th className="pb-1 pr-3 font-medium text-right">Disabled</th>
                  <th className="pb-1 pr-3 font-medium text-right">Skip · manual</th>
                </tr>
              </thead>
              <tbody>
                {refreshes.slice(0, 10).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 font-mono text-[11px] text-slate-600">
                      {new Date(r.refreshed_at).toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-700">{r.actor_email}</td>
                    <td className="py-1.5 pr-3 text-slate-700">{r.provider_key}</td>
                    <td className="py-1.5 pr-3 font-mono text-slate-700">{r.year}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-emerald-700">{r.inserted}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-amber-700">{r.updated}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-slate-700">{r.disabled}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-slate-500">{r.skipped_manual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function OverviewTile({ testid, icon: Icon, label, primary, secondary, loading, tone = "neutral" }) {
  const toneClass = tone === "ok" ? "border-emerald-200" : tone === "warn" ? "border-amber-300" : "border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-lg border bg-white p-3 ${toneClass}`}>
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <Icon size={12} weight="bold" />
        {label}
      </div>
      {loading ? (
        <div className="h-5 w-24 animate-pulse rounded bg-slate-100" />
      ) : (
        <>
          <div className="text-sm font-semibold text-slate-900">{primary}</div>
          {secondary && <div className="mt-0.5 text-[11px] text-slate-500">{secondary}</div>}
        </>
      )}
    </div>
  );
}

function UpcomingPanel({ testid, title, category, items, loading }) {
  const badge = CATEGORY_BADGE[category];
  return (
    <section data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold text-slate-900">{title}</h3>
      {loading ? (
        <div className="h-24 animate-pulse rounded bg-slate-100" />
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-xs text-slate-500">
          No upcoming {category === "statutory" ? "statutory" : "company"} holidays.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((h) => (
            <div
              key={h.id}
              data-testid={`${testid}-item-${h.id}`}
              className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: h.colour || (category === "statutory" ? "#4169E1" : "#228B22") }}
                />
                <div className="min-w-0">
                  <div className="truncate font-medium text-slate-900">{h.name}</div>
                  <div className="truncate text-[11px] text-slate-500">{badge?.label} · {h.region || "ON"}</div>
                </div>
              </div>
              <div className="shrink-0 font-mono text-[12px] text-slate-700">{formatDate(h.date)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ManagementSection({ testid, title, category, items, readOnly, onToggle, onRemove }) {
  const badge = CATEGORY_BADGE[category];
  return (
    <section data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{items.length}</span>
      </div>
      <div className="space-y-1">
        {items.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500">
            No {category === "statutory" ? "statutory" : "company"} holidays.
          </div>
        )}
        {items.map((h) => (
          <div
            key={h.id}
            data-testid={`wf-holiday-${category}-${h.id}`}
            className={`flex items-center justify-between rounded-md border px-3 py-1.5 text-sm ${h.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: h.colour || (category === "statutory" ? "#4169E1" : "#228B22") }}
              />
              <span className="font-mono text-[12px] text-slate-500">{formatDate(h.date)}</span>
              <span className="ml-1 truncate font-medium text-slate-900">{h.name}</span>
              <span className={`ml-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${badge?.tone}`}>
                {badge?.label}
              </span>
              {!h.active && (
                <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">disabled</span>
              )}
            </div>
            {!readOnly && (
              <div className="flex shrink-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => onToggle(h)}
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                >
                  {h.active ? "Disable" : "Enable"}
                </button>
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => onRemove(h.id)}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-800"
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function formatRelativeTs(iso) {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
